<template>
  <div v-if="contents">
    <div class="flex flex-wrap gap-y-6 gap-x-20 mt-5">
      <HubLogo :hub="hub" />
      <div v-if="!isInIframe" class="flex items-end flex-wrap gap-3">
        <Button
          :label="t('hub.favorite')"
          :right-icon-name="IconName.STAR"
          :size="ButtonSize.SM"
          :variant="ButtonVariant.OUTLINED"
          :color="ButtonColor.TERTIARY"
        />
      </div>
    </div>
    <CompletedContentsBanner
      v-if="isCompletedContentsBannerEligible && contents.totalCount > 0"
      v-model="filters"
      class="my-5"
      :hub="hub"
    />
    <FiltersWrapper v-model="filters" :hub="hub" />
    <div
      v-if="contents.nodes.length > 0"
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-8"
      data-testid="content-cards"
    >
      <ContentCard
        v-for="content in contents.nodes"
        :key="content.id"
        :content="content"
        data-testid="content-card"
      />
    </div>
    <EmptyList v-else class="mt-15" />

    <pagination
      v-if="contents.totalCount > 0"
      v-model="page"
      :per-page="perPage"
      :total-count="contents.totalCount"
      class="mt-8 text-right"
    />
  </div>
</template>

<script setup lang="ts">
import { useAsyncData } from 'nuxt/app';
import { computed, watch } from 'vue';
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  IconName,
  Pagination,
} from '@exfluential/ui';
import {
  apiGetContentsList,
  ContentType,
  GetContentsListQueryParams,
} from '~/api/contents';
import { onBeforeUnmount } from '#imports';
import { EmptyList } from '~/pages/_components/empty-list';
import { clearNuxtData } from '#app/composables/asyncData';
import { useLoader } from '~/composables/use-loader';
import { useI18n } from 'vue-i18n';
import { ContentCard } from '~/pages/_components/content-card';
import { handleApiError } from '~/api';
import { CompletedContentsBanner } from '~/pages/_components/hub-details/_components/completed-contents-banner';
import { LaunchMetricType } from '~/api/metrics';
import HubLogo from '../../_components/hub-homepage/_components/hub-logo/hub-logo.vue';
import { FiltersWrapper } from '../../_components/filters-wrapper';
import {
  stringTransformer,
  booleanTransformer,
  integerTransformer,
  useQueryParams,
  usePageQueryParam,
  arrayTransformer,
  enumTransformer,
} from '~/composables/use-query-param';
import { HubDto } from '~/api/hubs';
import { useIsInIframe } from '~/composables/use-is-in-iframe';
import { storeToRefs } from 'pinia';
import { useAccountStore } from '~/store/account';
import { AccountType } from '~/api/accounts';

type Props = {
  hub: HubDto;
};

const props = defineProps<Props>();

const { t } = useI18n();
const isInIframe = useIsInIframe();

const { currentAccount } = storeToRefs(useAccountStore());

const isCompletedContentsBannerEligible = computed(
  () =>
    props.hub.isLaunchEnabled &&
    currentAccount.value?.type === AccountType.STANDARD,
);

const page = usePageQueryParam();
const perPage = 12;

const filters = useQueryParams({
  search: {
    ...stringTransformer,
    defaultValue: '',
  },
  categoryId: {
    ...integerTransformer,
    defaultValue: null,
  },
  subcategoryId: {
    ...integerTransformer,
    defaultValue: null,
  },
  type: {
    ...arrayTransformer(enumTransformer(ContentType)),
    defaultValue: [],
  },
  tagIds: {
    ...arrayTransformer(integerTransformer),
    defaultValue: [],
  },
  status: {
    ...arrayTransformer(enumTransformer(LaunchMetricType)),
    defaultValue: [],
  },
  customizableOnly: {
    ...booleanTransformer,
    defaultValue: false,
  },
});

const queryParams = computed(() => {
  const query: GetContentsListQueryParams = {};

  if (
    filters.value.type.length > 0 &&
    filters.value.type.length < Object.values(ContentType).length
  ) {
    query.type = filters.value.type;
  }
  if (filters.value.tagIds.length > 0) {
    query.tagsIds = filters.value.tagIds;
  }
  if (
    filters.value.status.length > 0 &&
    filters.value.status.length < Object.values(LaunchMetricType).length
  ) {
    query.launchMetricTypes = filters.value.status;
  }
  if (filters.value.search.length > 0) {
    query.search = filters.value.search;
  }
  if (filters.value.categoryId !== null) {
    query.categoryId = filters.value.categoryId;
  }
  if (filters.value.subcategoryId !== null) {
    query.subcategoryId = filters.value.subcategoryId;
  }
  if (filters.value.customizableOnly) {
    query.customizableOnly = filters.value.customizableOnly;
  }

  return query;
});

const {
  data: contents,
  error: contentsError,
  refresh: refreshContents,
} = await useAsyncData(`contents-${props.hub.slug}`, () => {
  return apiGetContentsList(props.hub.slug, {
    ...queryParams.value,
    page: page.value,
    perPage,
  });
});

onBeforeUnmount(() => {
  clearNuxtData(`contents-${props.hub.slug}`);
});

if (contentsError.value) {
  await handleApiError(contentsError.value);
}

const reloadContentsList = async () => {
  await useLoader({
    async action() {
      await refreshContents();
    },
  });
};

watch(page, async () => {
  await reloadContentsList();
});
watch(
  queryParams,
  async () => {
    if (page.value === 1) {
      await reloadContentsList();
    } else {
      page.value = 1;
    }
  },
  { deep: true },
);
</script>
